/**
 * Batterijmeter voor een testtoestel — meten vóór en na een energiefix.
 *
 * Twee bronnen, in volgorde:
 *   1. de APK (`KeepAlive.getPowerStats`, vanaf APK 1.0.4 / versionCode 5): stroom,
 *      spanning, lading in µAh, temperatuur, schermhelderheid, APK-versie;
 *   2. de browser (`navigator.getBattery`): alleen niveau en laden ja/nee.
 *
 * Het verbruik wordt op twee manieren getoond, omdat de eerste niet op elk toestel klopt:
 *   - de ogenblikkelijke stroom (CURRENT_NOW). Android zegt µA, meerdere Samsung-tablets
 *     geven mA, en het teken verschilt per fabrikant. Daarom een eenhedenschatting en de
 *     richting uit `status`, nooit uit het teken;
 *   - de daling over tijd, uit de opgeslagen metingen. Die is fabrikantonafhankelijk en
 *     is het getal dat telt voor "haalt hij de nacht".
 *
 * Metingen staan in localStorage (één per minuut, 24 uur), zodat een nacht een herstart
 * van de WebView overleeft. Er gaat niets naar de server.
 */
import { normalizeCurrentMa, computeBatteryDrain } from '@caro/shared/telemetry';
import type { ConnectivitySample } from '@caro/shared/telemetry';
import { KeepAliveBridge, type NativePowerStats } from '../native/KeepAliveBridge';
import { logEvent } from './debugLog';

export interface PowerSample {
  /** epoch ms */
  t: number;
  /** 0–100 */
  level: number;
  /** resterende lading in µAh, of null als het toestel die niet geeft */
  uah: number | null;
  /** aan de lader (of vol) */
  plugged: boolean;
  /** genormaliseerde stroom in mA (absolute waarde), of null */
  ma: number | null;
}

export interface PowerView {
  source: 'native' | 'browser' | 'none';
  raw: NativePowerStats | null;
  levelPct: number | null;
  charging: boolean;
  plugged: string;
  currentMa: number | null;
  currentUnitGuess: 'µA' | 'mA' | null;
  watts: number | null;
  temperatureC: number | null;
  /** daling in %/uur over het venster, positief = ontladen; null = te weinig data */
  drainPctPerHour: number | null;
  drainWindowMin: number | null;
  /** geschatte uren tot leeg bij het huidige tempo */
  hoursLeft: number | null;
}

const SAMPLES_KEY = 'caro_power_samples';
const SAMPLE_EVERY_MS = 60_000;
const MAX_SAMPLES = 24 * 60;
const LOG_EVERY_MS = 15 * 60_000;
const INT_MIN = -2147483648;

export function loadSamples(): PowerSample[] {
  try {
    const raw = window.localStorage.getItem(SAMPLES_KEY);
    const arr = raw ? (JSON.parse(raw) as PowerSample[]) : [];
    return Array.isArray(arr) ? arr.slice(-MAX_SAMPLES) : [];
  } catch {
    return [];
  }
}

function saveSamples(samples: PowerSample[]) {
  try {
    window.localStorage.setItem(SAMPLES_KEY, JSON.stringify(samples.slice(-MAX_SAMPLES)));
  } catch {
    /* quota / unavailable */
  }
}

export function clearSamples() {
  try {
    window.localStorage.removeItem(SAMPLES_KEY);
  } catch {
    /* unavailable */
  }
}

async function readBrowserBattery(): Promise<NativePowerStats | null> {
  try {
    const nav = navigator as Navigator & { getBattery?: () => Promise<{ level: number; charging: boolean }> };
    if (!nav.getBattery) return null;
    const b = await nav.getBattery();
    return {
      levelPct: Math.round(b.level * 100),
      status: b.charging ? 'charging' : 'discharging',
      plugged: b.charging ? 'other' : 'none',
    };
  } catch {
    return null;
  }
}

/** Eén lezing, als weergave. Slaat een meting op als de vorige ouder is dan een minuut. */
export async function readPower(): Promise<PowerView> {
  const native = await KeepAliveBridge.getPowerStats();
  const raw = native ?? (await readBrowserBattery());
  const source: PowerView['source'] = native ? 'native' : raw ? 'browser' : 'none';

  const levelPct = raw?.levelPct ?? raw?.capacityPct ?? null;
  const charging = raw?.status === 'charging' || raw?.status === 'full';
  const pluggedIn = charging || (raw?.plugged != null && raw.plugged !== 'none');
  const current = normalizeCurrentMa(raw?.currentNowRaw);
  const watts =
    current && raw?.voltageMv && raw.voltageMv > 0 ? (current.ma * raw.voltageMv) / 1_000_000 : null;
  const temperatureC =
    raw?.temperatureDeciC != null && raw.temperatureDeciC !== INT_MIN ? raw.temperatureDeciC / 10 : null;

  const now = Date.now();
  let samples = loadSamples();
  if (levelPct != null) {
    const prev = samples[samples.length - 1];
    if (!prev || now - prev.t >= SAMPLE_EVERY_MS || prev.plugged !== pluggedIn) {
      const uah = raw?.chargeCounterUah != null && raw.chargeCounterUah > 0 ? raw.chargeCounterUah : null;
      samples = [...samples, { t: now, level: levelPct, uah, plugged: pluggedIn, ma: current?.ma ?? null }];
      saveSamples(samples);
      if (prev && prev.plugged !== pluggedIn) {
        logEvent('power.plug', { plugged: pluggedIn, level: levelPct, via: raw?.plugged });
      }
    }
  }

  const drain = computeBatteryDrain(samples, now);
  const drainPctPerHour = drain?.pctPerHour ?? null;
  const hoursLeft =
    drainPctPerHour != null && drainPctPerHour > 0.05 && levelPct != null ? levelPct / drainPctPerHour : null;

  const view: PowerView = {
    source,
    raw,
    levelPct,
    charging,
    plugged: raw?.plugged ?? 'unknown',
    currentMa: current?.ma ?? null,
    currentUnitGuess: current?.unit ?? null,
    watts,
    temperatureC,
    drainPctPerHour,
    drainWindowMin: drain?.windowMin ?? null,
    hoursLeft,
  };
  maybeLog(view);
  return view;
}

let lastLogAt = 0;
/** Om het kwartier een regel in het diagnoselogboek, zodat je 's ochtends de nacht terugleest. */
function maybeLog(v: PowerView) {
  const now = Date.now();
  if (now - lastLogAt < LOG_EVERY_MS) return;
  lastLogAt = now;
  logEvent('power', {
    level: v.levelPct,
    plugged: v.plugged,
    mA: v.currentMa != null ? Math.round(v.currentMa) : null,
    W: v.watts != null ? Number(v.watts.toFixed(2)) : null,
    drainPctH: v.drainPctPerHour != null ? Number(v.drainPctPerHour.toFixed(1)) : null,
    brightness: v.raw?.systemBrightness,
    screenOn: v.raw?.interactive,
    keepScreenOn: v.raw?.keepScreenOn,
  });
}

/**
 * Native batterijwaarden voor het connectiviteitsbericht (elke 60 s een meting, geflusht
 * bij een wijziging of per kwartier). Dat is wat het portaal bij de toestel-details
 * toont. Leeg in de browser en op een APK < 1.0.4, zodat een oudere build gewoon de
 * bestaande `batteryLevel` uit de Battery API blijft sturen.
 */
export async function powerTelemetry(): Promise<Partial<ConnectivitySample>> {
  const raw = await KeepAliveBridge.getPowerStats();
  if (!raw) return {};
  const out: Partial<ConnectivitySample> = {};
  const level = raw.levelPct ?? raw.capacityPct;
  if (typeof level === 'number' && level >= 0) out.batteryLevel = level;
  if (raw.status) out.batteryCharging = raw.status === 'charging' || raw.status === 'full';
  if (raw.plugged) out.batteryPlugged = raw.plugged;
  const current = normalizeCurrentMa(raw.currentNowRaw);
  if (current) out.batteryMa = Math.round(current.ma);
  if (raw.voltageMv != null && raw.voltageMv > 0) out.batteryMv = raw.voltageMv;
  if (raw.temperatureDeciC != null && raw.temperatureDeciC !== INT_MIN) out.batteryTempC = raw.temperatureDeciC / 10;
  if (raw.chargeCounterUah != null && raw.chargeCounterUah > 0) out.batteryUah = raw.chargeCounterUah;
  // De effectieve helderheid: een vensteroverride (standby-dimmen) wint van het systeem.
  if (raw.windowBrightness != null && raw.windowBrightness >= 0) {
    out.screenBrightness = Math.round(raw.windowBrightness * 255);
  } else if (raw.systemBrightness != null && raw.systemBrightness >= 0) {
    out.screenBrightness = raw.systemBrightness;
  }
  if (raw.interactive != null) out.screenOn = raw.interactive;
  if (raw.keepScreenOn != null) out.keepScreenOn = raw.keepScreenOn;
  if (raw.versionName) out.apkVersion = raw.versionName;
  if (raw.versionCode != null) out.apkVersionCode = raw.versionCode;
  return out;
}
